import {
    CSSProperties,
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
} from 'react'
import Grid from '@mui/material/Grid'
import NumberButton from '../NumberButton'
import { useState } from 'react'
import {
    Button,
    SxProps,
    Theme,
    Typography,
    useMediaQuery,
} from '@mui/material'
import Backspace from '@mui/icons-material/Backspace'
import Clear from '@mui/icons-material/Clear'
import SignButton from '../SignButton'
import { useAppSelector } from 'store/useStoreHook'
import './pin-login.scss'
import { useTranslation } from 'react-i18next'
import { UseKeyInputPINEventHandler } from 'hooks/useKeyInputPIN/useKeyInputPIN'
import { useScreenSize } from 'hooks/useScreenSize/useScreenSize'
import { useLoginContext } from 'pages/LoginPage/LoginProvider'

const NumpadInput = (onClickNumpad: (value: string) => void) => {
    let cols: any[] = []
    for (let i = 0; i < 3; i++) {
        let rows: any[] = []
        for (let j = 1; j < 4; j++) {
            rows.push(
                <Grid item key={`numpad_${i}${j}`}>
                    <NumberButton
                        onClick={onClickNumpad}
                        number={(i * 3 + j).toString()}
                    />
                </Grid>,
            )
        }
        cols.push(
            <Grid
                item
                spacing={2}
                container
                justifyContent="center"
                direction="row"
                alignItems="center"
                key={'numpad_row_' + i}
            >
                {[...rows]}
            </Grid>,
        )
    }
    return <>{cols}</>
}

interface PasswordInputIndicateProps {
    minLength: number
    currentLength: number
    isWrongPIN: boolean
}

const containerStyle: (isLandscapeMobile?: boolean) => SxProps<Theme> = (
    isLandscapeMobile,
) => ({
    padding: isLandscapeMobile ? '3px' : '10px',
    paddingTop: isLandscapeMobile ? '10px' : '12px',
    paddingBottom: isLandscapeMobile ? '2px' : '12px',
})

const PasswordInputIndicate = ({
    minLength,
    currentLength,
    isWrongPIN,
}: PasswordInputIndicateProps) => {
    const { isLandscapeMobile } = useScreenSize()
    const passwordArr = useMemo(
        () => new Array(currentLength).fill(0).map((_, idx) => idx + 1),
        [currentLength],
    )
    const mContainerStyle = useMemo(() => containerStyle(isLandscapeMobile), [
        isLandscapeMobile,
    ])
    const mButtonStyle: CSSProperties | undefined = useMemo(
        () =>
            isLandscapeMobile
                ? {
                      height: '10px',
                      width: '10px',
                  }
                : undefined,
        [isLandscapeMobile],
    )

    if (currentLength <= minLength) {
        return (
            <Grid
                container
                item
                justifyContent="center"
                direction="row"
                alignItems="center"
                xs={12}
            >
                {Array.from({ length: minLength }, (_, i) => (
                    <Grid sx={mContainerStyle} item key={`p-dot-${i}`}>
                        <span
                            className={
                                isWrongPIN
                                    ? 'login-password_dot--error'
                                    : i < currentLength
                                    ? 'login-password_dot--filled'
                                    : 'login-password_dot'
                            }
                            style={mButtonStyle}
                        ></span>
                    </Grid>
                ))}
            </Grid>
        )
    }

    return (
        <Grid
            container
            item
            justifyContent="center"
            direction="row"
            alignItems="center"
            xs={12}
        >
            {passwordArr.map((i) => (
                <Grid sx={mContainerStyle} item key={`p-dot-${i}`}>
                    <span
                        className={
                            isWrongPIN
                                ? 'login-password_dot--error'
                                : 'login-password_dot--filled'
                        }
                        style={mButtonStyle}
                    ></span>
                </Grid>
            ))}
        </Grid>
    )
}

const PinLogin = forwardRef<UseKeyInputPINEventHandler, any>((_, ref) => {
    const { t } = useTranslation()
    const { isLandscapeMobile, isMobile, isLandscapeTablet } = useScreenSize()

    const minPinLength =
        useAppSelector((state) => state.operation.data)?.minPinLength ?? 0
    const maxPinLength = 6

    const { loginWithPIN, loginError } = useLoginContext()

    const pin = useRef('')
    const [pinLength, setPinLength] = useState(0)

    const [isWrongPIN, setWrongPIN] = useState<boolean>(false)

    const isLowerThanMd = useMediaQuery((theme: any) =>
        theme.breakpoints.down('md'),
    )

    const isLowerThanLg = useMediaQuery((theme: any) =>
        theme.breakpoints.down('lg'),
    )

    const submitPin = useCallback(() => {
        if (pinLength >= minPinLength && pinLength <= maxPinLength) {
            loginWithPIN(pin.current)
        }
    }, [pinLength, minPinLength, loginWithPIN])

    const onClickNumpad = useCallback(
        (value: string) => {
            if (isWrongPIN) setWrongPIN(false)

            if (pinLength < maxPinLength) {
                pin.current += value
                setPinLength((prev) => prev + 1)
            }
        },
        [isWrongPIN, pinLength],
    )

    const clearPassword = useCallback(() => {
        pin.current = ''
        setPinLength(0)
    }, [])

    const onClickBack = useCallback(() => {
        if (isWrongPIN) setWrongPIN(false)
        if (pin.current.length > 0) {
            pin.current = pin.current.slice(0, -1)
            setPinLength((prev) => prev - 1)
        }
    }, [isWrongPIN])

    const onClickClear = useCallback(() => {
        if (isWrongPIN) setWrongPIN(false)
        clearPassword()
    }, [isWrongPIN, clearPassword])

    const numpadInputComponent = useMemo(() => NumpadInput(onClickNumpad), [
        onClickNumpad,
    ])
    const iconSize = useMemo(
        () =>
            isLowerThanMd || isLandscapeTablet ? 16 : isLowerThanLg ? 24 : 40,
        [isLowerThanMd, isLandscapeTablet, isLowerThanLg],
    )

    useEffect(() => {
        if (loginError) setWrongPIN(true)
    }, [loginError])

    // Set PIN Keyboard Input Handler
    useImperativeHandle(ref, () => ({
        onNumberKeyPress(key: string) {
            onClickNumpad(key)
        },
        onBackspacePress() {
            onClickBack()
        },
        onEnterPress() {
            submitPin()
        },
    }))

    return (
        <Grid
            container
            justifyContent="center"
            direction="column"
            alignItems="center"
        >
            <Grid item>
                <PasswordInputIndicate
                    minLength={minPinLength}
                    currentLength={pinLength}
                    isWrongPIN={isWrongPIN}
                />
            </Grid>
            <Grid item>
                <Grid
                    item
                    container
                    justifyContent="center"
                    direction="column"
                    alignItems="center"
                    spacing={!isLandscapeMobile ? 2 : 1}
                >
                    <Grid item container spacing={1}>
                        {numpadInputComponent}
                    </Grid>
                    <Grid item container spacing={1}>
                        <Grid
                            item
                            spacing={2}
                            container
                            justifyContent="center"
                            alignItems="center"
                        >
                            <Grid item>
                                <SignButton
                                    onClick={onClickClear}
                                    icon={
                                        <Clear
                                            style={{
                                                width: iconSize,
                                                height: iconSize,
                                            }}
                                        ></Clear>
                                    }
                                    label="Clear"
                                />
                            </Grid>
                            <Grid item>
                                <NumberButton
                                    onClick={onClickNumpad}
                                    number="0"
                                />
                            </Grid>
                            <Grid item>
                                <SignButton
                                    isActive={false}
                                    onClick={onClickBack}
                                    icon={
                                        <Backspace
                                            style={{
                                                width: iconSize,
                                                height: iconSize,
                                            }}
                                        ></Backspace>
                                    }
                                    label="Back"
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        sx={{
                            mx: 2,
                        }}
                        textAlign={'center'}
                    >
                        <Typography
                            color="error"
                            sx={{
                                fontSize: !isLandscapeMobile ? 18 : 12,
                            }}
                        >
                            {isWrongPIN && loginError ? t(loginError) : ''}
                        </Typography>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item mt={isMobile || isLandscapeTablet ? 1 : 3}>
                <Grid item>
                    <Button
                        size={isMobile ? 'small' : 'large'}
                        sx={{
                            backgroundColor: (theme) => theme.palette.info.main,
                            borderColor: (theme) => theme.palette.info.main,
                            color: '#fff',
                            fontSize: isMobile
                                ? '16px'
                                : isLandscapeTablet
                                ? '20px'
                                : '24px',
                            '&:hover': {
                                backgroundColor: (theme) =>
                                    theme.palette.info.dark,
                                borderColor: (theme) => theme.palette.info.dark,
                            },
                            '&:active': {
                                backgroundColor: (theme) =>
                                    theme.palette.info.main,
                                borderColor: (theme) => theme.palette.info.main,
                            },
                            width: '300px',
                        }}
                        fullWidth={true}
                        color="primary"
                        type="submit"
                        disableElevation
                        onClick={submitPin}
                        disabled={pinLength < minPinLength}
                    >
                        {t('login.button.signin')}
                    </Button>
                </Grid>
            </Grid>
        </Grid>
    )
})

export default PinLogin
